import { createContext, useContext } from 'react';
import type { LanguageCode } from '@/config/languages';

export interface LocaleContextValue {
  /** The declaration code (`en es pt fr zh`) — storage, folders, URLs. */
  locale: LanguageCode;
  /** The BCP 47 tag — `<html lang>`, `Intl.*`, `:lang()`. */
  bcp47: string;
  setLocale: (code: LanguageCode) => Promise<void>;
  isEnglish: boolean;
  /**
   * True while the active role is staff (TRFer or admin): the UI is forced to
   * English and the language pickers stand down (DECISIONS 2026-09-30).
   * Optional so a test that provides the context by hand reads as "not staff".
   */
  staffEnglishOnly?: boolean;
}

/**
 * The active roles whose pages are English-only (DECISIONS 2026-09-30:
 * Victor ruled TRF staff and admin pages English-only; every TRFer and admin
 * works in English). Keyed on the ACTIVE role (`user.role`), not on
 * `user.roles`, so the demo account that switches to Parent gets its saved
 * language back on the parent pages.
 */
export const ENGLISH_ONLY_ROLES: readonly string[] = ['trfer', 'admin'];

export function isEnglishOnlyRole(role: string | null | undefined): boolean {
  return !!role && ENGLISH_ONLY_ROLES.includes(role);
}

/** Provided by `LocaleProvider`; lives here so that file exports only a component (react-refresh). */
export const LocaleContext = createContext<LocaleContextValue | null>(null);

const ENGLISH_ONLY: LocaleContextValue = {
  locale: 'en',
  bcp47: 'en',
  setLocale: async () => {},
  isEnglish: true,
  staffEnglishOnly: false,
};

/**
 * `{ locale, bcp47, setLocale, isEnglish }` for the active UI locale.
 *
 * Outside a `LocaleProvider` — every component test that mounts a surface
 * without the app shell — it reports English, which is the test locale
 * (rulings brief B24), rather than throwing.
 */
export function useLocale(): LocaleContextValue {
  return useContext(LocaleContext) ?? ENGLISH_ONLY;
}
